import React, { useEffect, useState } from "react";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from "recharts";
import { useRecoilValue } from "recoil";
import { filtersAtom } from "../state/filter";
import { useAuth } from "./AuthContext";
import { API_BASE_URL } from "../config/api";

function getMonthLabel(dateStr: string) {
    const d = new Date(dateStr);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function normalizeDate(d: Date) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function getPurchaseDate(item: any): Date {
    return new Date(item?.transactionDate || item?.createdAt);
}

function isHiddenTransaction(item: any): boolean {
    const productPrice = parseFloat(item?.productPrice ?? "0");
    const carePrice = parseFloat(item?.carePrice ?? "0");
    const isProductNegative = productPrice < 0;
    const isCarePriceZero = carePrice === 0;
    const hasNoRoot = item?.rootUniqueId === null || item?.rootUniqueId === undefined || item?.rootUniqueId === "";
    const isUnsold = item?.sold === false;

    return isProductNegative && isCarePriceZero && hasNoRoot && isUnsold;
}

function isCarePlusReplacement(item: any, allItems: any[]): boolean {
    if (item?.status !== "created" || item?.sold !== true) return false;

    const hasLink =
        (item?.eventId != null && item.eventId !== "") ||
        (item?.rootUniqueId != null && item.rootUniqueId !== "");
    if (!hasLink) return false;

    const originalExchanged =
        (item.eventId &&
            allItems.find(
                (i: any) => i?.eventId === item.eventId && i?.status === "exchanged",
            )) ||
        allItems.find(
            (i: any) =>
                i?.status === "exchanged" &&
                (i?.uniqueId === item.rootUniqueId || i?.rootUniqueId === item.rootUniqueId),
        );

    return Boolean(originalExchanged && originalExchanged.sold === true);
}

function isOptIn(item: any, allItems: any[] = []): boolean {
    if (item?.sold === true && item?.status === "exchanged") return true;

    if (item?.sold === true && item?.status === "created") {
        const hasRoot =
            item?.rootUniqueId !== null &&
            item?.rootUniqueId !== undefined &&
            item?.rootUniqueId !== "";

        if (!hasRoot) return true;
        if (!isCarePlusReplacement(item, allItems)) return true;
    }

    return false;
}

function isCancellationReversal(item: any): boolean {
    const carePrice = parseFloat(item?.carePrice ?? 0);

    if (!(carePrice < 0 && item?.sold === false)) return false;
    if (item?.status === "ReturnedForExchange") return false;

    return item?.status === "created" || item?.status === "cancelled";
}

/**
 * Revenue ledger (same as SummaryGrid):
 * opt-ins + replacements + ReturnedForExchange + cancellation reversals + cancelled billed originals
 */
function shouldCountRevenue(item: any, allItems: any[] = []): boolean {
    if (isOptIn(item, allItems)) return true;
    if (isCarePlusReplacement(item, allItems)) return true;
    if (item?.status === "ReturnedForExchange") return true;
    if (isCancellationReversal(item)) return true;
    if (item?.sold === true && item?.status === "cancelled") return true;
    return false;
}

type ChartData = { month: string; totalRevenue: number; optIns: number };

export const MonthlyRevenueChart = () => {
    const { user } = useAuth();
    const filters = useRecoilValue(filtersAtom);
    const [data, setData] = useState<ChartData[]>([]);
    const [loading, setLoading] = useState(true);
    const isAdmin = user?.userName === "omkark@securedstitch.com";

    useEffect(() => {
        let interval: NodeJS.Timeout;
        const fetchSales = async () => {
            try {
                let allItems: any[] = [];
                if (isAdmin) {
                    const res = await fetch(`${API_BASE_URL}/api/sale`);
                    if (!res.ok) return;
                    allItems = await res.json();
                    if (!Array.isArray(allItems)) allItems = [];
                    if (filters.member) {
                        allItems = allItems.filter((item: any) => item.memberUuid === filters.member);
                    }
                } else {
                    const memberUuid = localStorage.getItem("memberUuid") || "";
                    if (!memberUuid) return;
                    const res = await fetch(`${API_BASE_URL}/api/crd/sales`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ memberUuid }),
                    });
                    if (!res.ok) return;
                    allItems = await res.json();
                    if (!Array.isArray(allItems)) allItems = [];
                }

                let items = allItems.filter((item: any) => !isHiddenTransaction(item));

                if (filters.fromDate && filters.toDate) {
                    const from = normalizeDate(filters.fromDate);
                    const to = normalizeDate(filters.toDate);
                    items = items.filter((item: any) => {
                        const d = normalizeDate(getPurchaseDate(item));
                        return d >= from && d <= to;
                    });
                }

                const monthly: Record<string, { totalRevenue: number; optIns: number }> = {};

                items.forEach((item: any) => {
                    const isOptedIn = isOptIn(item, allItems);
                    const countRevenue = shouldCountRevenue(item, allItems);

                    if (!isOptedIn && !countRevenue) return;

                    const dateStr = item.transactionDate || item.createdAt;
                    const month = getMonthLabel(dateStr);
                    if (!monthly[month]) monthly[month] = { totalRevenue: 0, optIns: 0 };

                    if (countRevenue) {
                        monthly[month].totalRevenue += parseFloat(item.carePrice) || 0;
                    }

                    // Opt-ins only for True / exchanged opt-in rows (not Replacement-only, not returns)
                    if (isOptedIn) {
                        monthly[month].optIns += 1;
                    }
                });

                const chartData = Object.entries(monthly).map(([month, v]) => ({
                    month,
                    totalRevenue: parseFloat(v.totalRevenue.toFixed(2)),
                    optIns: v.optIns,
                }));
                chartData.sort((a, b) => a.month.localeCompare(b.month));
                setData(chartData);
            } catch (error) {
                setData([]);
            } finally {
                setLoading(false);
            }
        };
        fetchSales();
        interval = setInterval(fetchSales, 10000);
        return () => clearInterval(interval);
    }, [isAdmin, filters.member, filters.fromDate, filters.toDate]);

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (!active || !payload?.length) return null;
        return (
            <div className="bg-[#1a1a1a] border border-[#2d2d2d] p-3 rounded-lg shadow-xl">
                <p className="text-white font-semibold mb-2 border-b border-[#2d2d2d] pb-2">{label}</p>
                <div className="flex justify-between gap-4">
                    <span className="text-gray-400">Total Revenue</span>
                    <span className="text-yellow-400 font-medium">₹{payload[0]?.value?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between gap-4">
                    <span className="text-gray-400">Opt-ins</span>
                    <span className="text-purple-400 font-medium">{payload[1]?.value}</span>
                </div>
            </div>
        );
    };

    return (
        <div className="bg-neutral-900 p-6 rounded-xl w-full h-[300px]">
            <h2 className="text-white mb-4 font-semibold">Monthly Revenue & Opt-ins</h2>
            {loading ? (
                <div className="flex items-center justify-center h-full">
                    <div className="text-gray-400">Loading...</div>
                </div>
            ) : data.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                    <div className="text-gray-400">No data available</div>
                </div>
            ) : (
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                        <XAxis dataKey="month" stroke="#ccc" tick={{ fill: "#ccc", fontSize: 11 }} />
                        <YAxis
                            stroke="#ccc"
                            yAxisId="revenue"
                            orientation="left"
                            tickFormatter={(v) => `₹${v.toFixed(2)}`}
                            tick={{ fill: "#ccc", fontSize: 11 }}
                        />
                        <YAxis
                            stroke="#ccc"
                            yAxisId="optins"
                            orientation="right"
                            tick={{ fill: "#ccc", fontSize: 11 }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Line
                            type="monotone"
                            dataKey="totalRevenue"
                            stroke="#ffd700"
                            name="Total Revenue"
                            yAxisId="revenue"
                            dot={false}
                            strokeWidth={2}
                        />
                        <Line
                            type="monotone"
                            dataKey="optIns"
                            stroke="#8884d8"
                            name="Opt-ins"
                            yAxisId="optins"
                            dot={false}
                            strokeWidth={2}
                        />
                    </LineChart>
                </ResponsiveContainer>
            )}
        </div>
    );
};